import { Matrix4, type BufferGeometry } from 'three';
import type { LinearRGB } from '../../core/color';
import type { Vec2, Vec3 } from '../../geo/types';
import { layoutToWorld } from '../../core/coords';
import {
  createMeshBuilder,
  type Frame,
  type MeshBuilder,
  type VertexStyle,
} from '../mesh/meshBuilder';
import { SURFACE } from '../mesh/attributes';
import type { PalisadeData, PalisadeSpan } from './palisade';
import { sparSolid } from './shipSolids';
import { SHIP_PAINTER_SCALE_H, SHIP_PAINTER_SCALE_V } from './shipPlacement';
import { nearestRow } from './rowSnap';

/*
 * The painting stands a round timber blockhouse, open at the top, on the inner pile row where
 * the pile jetty from the city quay meets it (scan 5020-5190 x 3460-3625); the woodcut draws it
 * round and open, two tiers of gun ports in its wall, square above and round below (woodcut
 * 2820-3020 x 2700-2850). Fitted to the painted outline through the painter camera and warp at
 * the painter scale of the ships about it (docs/plans/ships.md 14): 12.2 m across, the rim
 * 4.2 m above the water.
 */
export const BLOCKHOUSE = {
  radius: 6.1,
  wall: 0.4,
  height: 4.2,
  /** The floor inside, at the sills of the lower ports. */
  floor: 0.4,
  /** Below the water, so no seam shows where the wall meets it. */
  foot: -0.5,
  sides: 32,
} as const;

export const BLOCKHOUSE_PAINTER_SCALE: readonly [number, number] = [
  SHIP_PAINTER_SCALE_H,
  SHIP_PAINTER_SCALE_V,
];

/* The woodcut's front shows three round ports low and four square ones high across half the
   ring (woodcut 2870-2952 x 2772-2800), the square tier at 0.71 of the wall's height and the
   round one at 0.18, the round ports 0.06 of the ring across; eight a tier round the whole
   ring, each tier between the other's. */
export const BLOCKHOUSE_PORTS = {
  count: 8,
  lower: { y: 0.75, radius: 0.35, sides: 8 },
  upper: { y: 3.0, half: 0.28 },
  sunk: 0.02,
  proud: 0.03,
} as const;

/** A blockhouse further than this from every row is not on the palisade the layout has. */
export const MAX_BLOCKHOUSE_SNAP_M = 20;

export interface BlockhouseData {
  readonly id: string;
  /** Layout ground under the centre of the painted plan. */
  readonly centre: Vec2;
}

/* The centre, radius and height that carry a round wall at the ships' painter scale through the
   painter camera and the committed residual warp (tools/layout/painterframe, 2026-09-28) onto the
   painted outline: its sides at scan x 5023 and 5187, its base at y 3620, the front of its rim
   at 3563. */
export const BLOCKHOUSES: readonly BlockhouseData[] = [
  { id: 'jetty-blockhouse', centre: [357.1, 622.2] },
];

export interface PlacedBlockhouse {
  readonly id: string;
  readonly palisadeId: string;
  readonly position: Vec2;
  /** Metres along its palisade's path. */
  readonly station: number;
  /** The stretch of its row inside its wall, where the row stands no piles. */
  readonly opening: PalisadeSpan;
}

/** Each blockhouse on the row nearest the ground under it, astride that row. */
export function placeBlockhouses(
  blockhouses: readonly BlockhouseData[],
  palisades: readonly PalisadeData[],
  warn: (message: string) => void
): readonly PlacedBlockhouse[] {
  return blockhouses.flatMap((blockhouse) => {
    const nearest = nearestRow(palisades, blockhouse.centre);
    if (!nearest || nearest.distance > MAX_BLOCKHOUSE_SNAP_M) {
      warn(`Blockhouse ${blockhouse.id} stands off every palisade`);
      return [];
    }
    const { radius } = BLOCKHOUSE;
    return [
      {
        id: blockhouse.id,
        palisadeId: nearest.palisade.id,
        position: nearest.point,
        station: nearest.station,
        opening: { from: nearest.station - radius, to: nearest.station + radius },
      },
    ];
  });
}

/** The instance matrix: origin on the water at the centre of the plan. */
export function blockhouseMatrix(blockhouse: PlacedBlockhouse): Matrix4 {
  const [x, y, z] = layoutToWorld(blockhouse.position, 0);
  return new Matrix4().makeTranslation(x, y, z);
}

/** True where a layout point lies inside a blockhouse's wall as the painter view draws it. */
export function hiddenByBlockhouse(blockhouses: readonly PlacedBlockhouse[], p: Vec2): boolean {
  const reach = BLOCKHOUSE.radius * SHIP_PAINTER_SCALE_H;
  return blockhouses.some(
    ({ position }) => Math.hypot(p[0] - position[0], p[1] - position[1]) < reach
  );
}

function style(color: LinearRGB, edgeId: number, seed: number): VertexStyle {
  return { color, surface: SURFACE.wood, edgeId, seed, strokeAngle: 0, extent: [0, 0] };
}

const LOCAL: Frame = { origin: [0, 0, 0], yaw: 0 };
const STEP = (2 * Math.PI) / BLOCKHOUSE.sides;

/* The ring closes on its first corner itself, not on one a full turn round that rounding leaves
   a hair off it. */
function around(radius: number, y: number, k: number): Vec3 {
  const at = (k % BLOCKHOUSE.sides) * STEP;
  return [radius * Math.cos(at), y, radius * Math.sin(at)];
}

/* The wall's section from its foot outside, up, over the rim and down inside to the floor: each
   strip between two sections then faces out of the solid (MeshBuilder.quad). */
function wallProfile(): readonly (readonly [number, number])[] {
  const { radius, wall, height, floor, foot } = BLOCKHOUSE;
  return [
    [radius, foot],
    [radius, height],
    [radius - wall, height],
    [radius - wall, floor],
  ];
}

function buildShell(builder: MeshBuilder, wood: VertexStyle): void {
  const profile = wallProfile();
  const { sides, radius, wall, floor, foot } = BLOCKHOUSE;
  for (let k = 0; k < sides; k++) {
    for (let j = 0; j + 1 < profile.length; j++) {
      const [[r0, y0], [r1, y1]] = [profile[j]!, profile[j + 1]!];
      const quad = [around(r0, y0, k), around(r1, y1, k), around(r1, y1, k + 1)] as const;
      builder.quad(wood, LOCAL, [...quad, around(r0, y0, k + 1)]);
    }
  }
  const ring = (r: number, y: number): Vec3[] =>
    Array.from({ length: sides }, (_, k) => around(r, y, k));
  builder.polygon(wood, LOCAL, ring(radius, foot));
  builder.polygon(wood, LOCAL, ring(radius - wall, floor).reverse());
}

/* Each port sits on the middle of a face of the wall, sunk into it and standing proud of it. */
function buildPorts(builder: MeshBuilder, port: VertexStyle): void {
  const { count, lower, upper, sunk, proud } = BLOCKHOUSE_PORTS;
  const face = BLOCKHOUSE.radius * Math.cos(STEP / 2);
  const every = BLOCKHOUSE.sides / count;
  for (let i = 0; i < count; i++) {
    const low = (i * every + 0.5) * STEP;
    const high = (i * every + every / 2 + 0.5) * STEP;
    const out = (r: number, y: number): Vec3 => [r * Math.cos(low), y, r * Math.sin(low)];
    sparSolid(builder, port, {
      from: out(face - sunk, lower.y),
      to: out(face + proud, lower.y),
      radiusFrom: lower.radius,
      radiusTo: lower.radius,
      sides: lower.sides,
      painterWidthScale: 1,
    });
    const frame: Frame = { origin: [0, 0, 0], yaw: -high };
    const min: Vec3 = [face - sunk, upper.y - upper.half, -upper.half];
    const max: Vec3 = [face + proud, upper.y + upper.half, upper.half];
    builder.box(port, frame, min, max);
  }
}

/** The blockhouse in its own frame, origin on the water at the centre of its plan. */
export function buildBlockhouse(
  wood: LinearRGB,
  port: LinearRGB,
  edgeId: number,
  seed: number
): BufferGeometry {
  const builder = createMeshBuilder();
  buildShell(builder, style(wood, edgeId, seed));
  buildPorts(builder, style(port, edgeId, seed));
  return builder.build();
}
